// File: src/pages/Wallet.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../api";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";
import { baseUrl } from "../../Environments/environment";
import { toast } from "react-toastify";
import { Switch } from "@headlessui/react";
import Edit from "../../Assets/edit.svg";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

/**
 * DESIGN REFERENCE (image file):
 * /mnt/data/60c16f84-f0dd-48a7-b827-dc393b48552d.png
 *
 * Wallet page shows current credit, auto-topup settings and Add Credits button
 */

export default function Wallet() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [list, setList] = useState([]);
  const { selectedPropertyId } = useProperty();
  const propertyId = selectedPropertyId;

  const formatAmount = (cents) => `$${(cents / 100).toFixed(2)}`;
  const formatDate = (ts) => new Date(ts).toLocaleString();

  const badge = (status) => {
    switch (status) {
      case "succeeded":
        return (
          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded bg-green-100 text-green-700 whitespace-nowrap">
            Succeeded
          </span>
        );
      case "failed":
        return (
          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded bg-red-100 text-red-700 whitespace-nowrap">
            Failed
          </span>
        );
      default:
        return (
          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded bg-yellow-100 text-yellow-700 whitespace-nowrap">
            Pending
          </span>
        );
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${baseUrl}/stripe/wallet/${propertyId}`
      );
      setWallet(response.data.result);
    } catch (err) {
      console.error("Failed to load wallet", err);
    } finally {
      setLoading(false);
    }
  };
  const loadRecentTrans = async () => {
    try {
      const response = await apiClient.get(
        `${baseUrl}/stripe/transactions/${propertyId}?recent=${true}`
      );
      const data = response.data.result;
      setList(data || []);
    } catch (err) {
      console.error("Failed to load transactions:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    loadRecentTrans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openBillingPortal = async () => {
    try {
      const response = await apiClient.post(
        `${baseUrl}/stripe/billing-portal-session`,
        {
          customerId: wallet.stripeCustomerId,
          propertyId,
        }
      );
      window.open(response.data.result.url, "_blank");
    } catch (err) {
      console.error(err);
      toast.error("Failed to open billing portal");
    }
  };

  const handleUpdateWallet = async () => {
    try {
      if (wallet.topUpCredit < 500) {
        toast.error("Minimum Top Up is 500 credits.");
        return;
      } else if (wallet.lowThreshold < 100) {
        toast.error("Minimum Low Threshold is 100 credits.");
        return;
      }
      const response = await apiClient.put(`${baseUrl}/stripe/wallet`, wallet);

      if (response.data.statusCode === 200) {
        toast.success("Updated Wallet setting successfully!");
        load();
        setIsEditOpen(false);
      }
    } catch (error) {
      console.error("Error while Updating Wallet setting :", error);
      toast.error(
        error.response?.data?.message || "Failed to update Wallet setting"
      );
    }
  };

  const low = wallet?.currentCredit < wallet?.lowThreshold;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex flex-row items-center justify-between gap-2 sm:gap-4 mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold whitespace-nowrap">Wallet</h1>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {loading ? (
            <>
              <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-8 w-24 sm:w-28 bg-gray-200 rounded animate-pulse"></div>
            </>
          ) : (
            <>
              {!isEditOpen && (
                <button
                  className="text-indigo-600 hover:text-indigo-900 text-xl flex-shrink-0"
                  onClick={(e) => setIsEditOpen(true)}
                  aria-label="Edit wallet settings"
                >
                  <img src={Edit} alt="Edit" className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
              <button
                onClick={() => navigate("/dashboard/add-credits")}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-indigo-600 text-white rounded-md text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
              >
                Add Credits
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          {/* Skeleton for Current Balance Card */}
          <div className="p-3 sm:p-4 border rounded-md bg-white shadow-sm">
            <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-pulse"></div>
            <div className="h-8 sm:h-10 w-32 bg-gray-200 rounded mb-4 animate-pulse"></div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-6 w-11 bg-gray-200 rounded-full animate-pulse"></div>
            </div>
          </div>

          {/* Skeleton for Threshold/Top-up Card */}
          <div className="p-3 sm:p-4 border rounded-md bg-white shadow-sm">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-200 rounded mb-2 animate-pulse"></div>
                <div className="h-6 w-24 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div className="flex-1">
                <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-pulse"></div>
                <div className="h-6 w-24 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          {/* CURRENT BALANCE */}
          <div className="p-3 sm:p-4 border rounded-md bg-white shadow-sm">
            <div className="text-xs sm:text-sm text-gray-500">Current balance</div>
            <div className="text-2xl sm:text-3xl font-bold mt-1">
              {wallet?.credits ?? 0} credits
            </div>

          {/* AUTO TOP-UP WITH TOOLTIP */}
          <div className="text-xs sm:text-sm text-gray-500 mt-3 flex items-center gap-2 group relative flex-wrap">
            <label className="text-gray-800 font-semibold">Auto Top-up:</label>

            {/* Tooltip Icon */}
            <InformationCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 group-hover:text-indigo-600 cursor-pointer flex-shrink-0" />

            {/* Tooltip box */}
            <div
              className="absolute left-0 top-6 z-10
        hidden group-hover:block
        bg-black text-white text-xs px-2 py-1 rounded shadow-md w-max max-w-[200px] sm:max-w-none"
            >
              Automatically buys credits when balance drops below threshold.
            </div>

            <Switch
              checked={wallet?.isAutoTopUpEnabled}
              disabled={!isEditOpen}
              onChange={(value) =>
                setWallet({ ...wallet, isAutoTopUpEnabled: value })
              }
              className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer
        rounded-full border-2 border-transparent bg-gray-300 transition-colors duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2
        data-[checked]:bg-indigo-600"
            >
              <span className="sr-only">Enable auto top-up</span>
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block h-5 w-5 transform rounded-full 
          bg-white shadow ring-0 transition duration-200 ease-in-out 
          group-data-[checked]:translate-x-5"
              />
            </Switch>
          </div>
        </div>

        {/* THRESHOLD + TOP UP AMOUNT */}
        <div className="p-3 sm:p-4 border rounded-md bg-white shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between gap-4 sm:gap-x-4">
            {/* LOW THRESHOLD FIELD WITH TOOLTIP */}
            <div className="relative flex-1">
              <div className="flex items-center gap-1 group">
                <span className="text-xs sm:text-sm text-gray-500">
                  Auto top-up threshold
                </span>

                <InformationCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 group-hover:text-indigo-600 cursor-pointer flex-shrink-0" />

                {/* Tooltip */}
                <div
                  className="absolute left-0 top-6 hidden group-hover:block z-10
            bg-black text-white text-xs px-2 py-1 rounded shadow-md w-max max-w-[200px] sm:max-w-none"
                >
                  When your credits fall below this, auto top-up triggers.
                </div>
              </div>

              <div className="text-base sm:text-lg font-medium mt-1">
                {!isEditOpen && `${wallet?.lowThreshold} credits`}

                {isEditOpen && (
                  <input
                    type="number"
                    value={wallet.lowThreshold}
                    min={1}
                    onChange={(e) =>
                      setWallet({
                        ...wallet,
                        lowThreshold: e.target.value,
                      })
                    }
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border rounded-md mt-1 text-sm sm:text-base
                focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                )}
              </div>
            </div>

            {/* TOP UP AMOUNT WITH TOOLTIP */}
            <div className="relative flex-1">
              <div className="flex items-center gap-1 group">
                <span className="text-xs sm:text-sm text-gray-500">Top-up credits</span>

                <InformationCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 group-hover:text-indigo-600 cursor-pointer flex-shrink-0" />

                {/* Tooltip */}
                <div
                  className="absolute left-0 top-6 hidden group-hover:block z-10
            bg-black text-white text-xs px-2 py-1 rounded shadow-md w-max max-w-[200px] sm:max-w-none"
                >
                  Number of credits added automatically
                  <br />
                  when auto top-up triggers.
                </div>
              </div>

              <div className="text-base sm:text-lg font-medium mt-1">
                {!isEditOpen && `${wallet?.topUpCredit} credits`}

                {isEditOpen && (
                  <input
                    type="number"
                    value={wallet.topUpCredit}
                    min={1}
                    onChange={(e) =>
                      setWallet({
                        ...wallet,
                        topUpCredit: e.target.value,
                      })
                    }
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border rounded-md mt-1 text-sm sm:text-base
                focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Save / Cancel Buttons */}
          {isEditOpen && (
            <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2">
              <button
                onClick={() => {
                  setIsEditOpen(false);
                  load();
                }}
                className="px-3 sm:px-4 py-1.5 sm:py-2 border rounded-md hover:bg-gray-100 text-sm sm:text-base w-full sm:w-auto"
              >
                Cancel
              </button>

              <button
                onClick={handleUpdateWallet}
                className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 text-sm sm:text-base w-full sm:w-auto"
              >
                Save
              </button>
            </div>
          )}
        </div>
      </div>
      )}

      {loading ? (
        <>
          {/* Skeleton for Warning */}
          <div className="mb-4 p-3 sm:p-4 bg-gray-50 border-l-4 border-gray-200 rounded animate-pulse">
            <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
            <div className="h-3 w-full bg-gray-200 rounded"></div>
          </div>

          {/* Skeleton for Buttons */}
          <div className="mb-4 sm:mb-6 flex flex-row items-center gap-2 sm:gap-3">
            <div className="h-9 w-40 sm:w-44 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-9 w-32 sm:w-36 bg-gray-200 rounded animate-pulse"></div>
          </div>

          {/* Skeleton for Table */}
          <div className="mt-6 sm:mt-8">
            <div className="h-6 w-32 bg-gray-200 rounded mb-3 animate-pulse"></div>
            <div className="border rounded-md bg-white overflow-hidden">
              <div className="p-3 sm:p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex gap-4">
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {low && (
            <div className="mb-4 p-3 sm:p-4 bg-yellow-50 border-l-4 border-yellow-300 rounded">
              <div className="font-medium text-sm sm:text-base text-yellow-800">Low credit balance</div>
              <div className="text-xs sm:text-sm text-yellow-700 mt-1">
                Your balance is below the low threshold. Add credits now or enable
                auto-top-up.
              </div>
            </div>
          )}

          <div className="mb-4 sm:mb-6 flex flex-row items-center gap-2 sm:gap-3 overflow-x-auto">
            <button
              onClick={openBillingPortal}
              className="px-3 sm:px-4 py-1.5 sm:py-2 border rounded-md text-xs sm:text-sm whitespace-nowrap flex-shrink-0 hover:bg-gray-50"
            >
              Manage Payment Methods
            </button>
            <button
              onClick={() => navigate("/dashboard/transactions")}
              className="px-3 sm:px-4 py-1.5 sm:py-2 border rounded-md text-xs sm:text-sm whitespace-nowrap flex-shrink-0 hover:bg-gray-50"
            >
              View Transactions
            </button>
          </div>

          <div className="mt-6 sm:mt-8">
            <h2 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">Recent activity</h2>
            {/* Simple placeholder — you can expand by fetching transactions */}

            {list.length === 0 ? (
              <div className="p-3 sm:p-4 border rounded-md text-xs sm:text-sm text-gray-600">
                No recent activity to show.
              </div>
            ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0 border rounded-md bg-white">
            <div className="inline-block min-w-full align-middle px-4 sm:px-0">
              <table className="min-w-full text-xs sm:text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Type</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Credits</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Amount</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-900">Date</th>
                    {/* <th className="px-4 py-2 text-left">Action</th> */}
                  </tr>
                </thead>

                <tbody>
                  {list.map((t) => (
                    <tr key={t._id} className="border-b hover:bg-gray-50">
                      <td className="px-2 sm:px-4 py-2 sm:py-3 capitalize whitespace-nowrap">
                        {t.type.replace("_", " ")}
                      </td>

                      <td className="px-2 sm:px-4 py-2 sm:py-3 font-medium whitespace-nowrap">{t.credits}</td>

                      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">{formatAmount(t.amountCents)}</td>

                      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">{badge(t.status)}</td>

                      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs">{formatDate(t.createdAt)}</td>

                      {/* <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/transactions/${t._id}`)}
                        className="text-indigo-600 hover:underline"
                      >
                        View
                      </button>
                    </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
